import _ from "lodash";

export function isBundle(device) {
    // Return false if device is null, undefined, or not an object
    if (!device || typeof device !== 'object') {
        return false;
    }

    // Return true if the bundle_id is set. This is how we know it's a bundle.
    return !!(device.bundle_id || device.bundle);
}

export function isPart(device) {
    // Return false if device is null, undefined, or not an object
    if (!device || typeof device !== 'object') {
        return false;
    }

    // JSON blobs returned from ItemsController::list is a JSON-formatted object with `device` or `part`
    // in the top-level of the hash. If it's present, just return that
    if (device.part && !_.isEmpty(device.part)) {
        return true;
    }

    const deviceTypeDefinition = device.device_type_definition || device.type?.device_type_definition;

    if (!deviceTypeDefinition || typeof deviceTypeDefinition !== 'string') {
        return false;
    }

    // Check if the device_type_definition starts with "part!" prefix
    if (deviceTypeDefinition.startsWith('part!')) {
        return true;
    }

    // Check if the device_type_definition starts with "device!" prefix
    if (deviceTypeDefinition.startsWith('device!')) {
        return false;
    }

    // Fallback to legacy logic for backward compatibility during migration
    const manufacturerId = getManufacturerId(deviceTypeDefinition);
    const modelId = getModelId(deviceTypeDefinition);

    // Return true if this item does not have a manufacturer or model
    return manufacturerId === null && modelId === null;
}

export function isDevice(device) {
    // Return false if device is null, undefined, or not an object
    if (!device || typeof device !== 'object') {
        return false;
    }

    // JSON blobs returned from ItemsController::list is a JSON-formatted object with `device` or `part`
    // in the top-level of the hash. If it's present, just return that
    if (device.device && !_.isEmpty(device.device)) {
        return true;
    }

    const deviceTypeDefinition = device.device_type_definition || device.type?.device_type_definition;

    if (!deviceTypeDefinition || typeof deviceTypeDefinition !== 'string') {
        return false;
    }

    // Check if the device_type_definition starts with "device!" prefix
    if (deviceTypeDefinition.startsWith('device!')) {
        return true;
    }

    // Check if the device_type_definition starts with "part!" prefix
    if (deviceTypeDefinition.startsWith('part!')) {
        return false;
    }

    // Fallback to legacy logic for backward compatibility during migration
    const manufacturerId = getManufacturerId(deviceTypeDefinition);
    const modelId = getModelId(deviceTypeDefinition);

    // Return true if this item has a manufacturer and model
    return manufacturerId !== null && modelId !== null;
}

export function getDeviceTypeId(deviceTypeDefinitions) {
    if (deviceTypeDefinitions === undefined) {
        return null;
    }

    if (deviceTypeDefinitions.trim() === "") {
        return null;
    }

    // Split this ID
    const deviceTypeDefinitionId = deviceTypeDefinitions.split('!');

    // Check if this is a prefixed format (device! or part!)
    if (deviceTypeDefinitionId.length > 1 && (deviceTypeDefinitionId[0] === 'device' || deviceTypeDefinitionId[0] === 'part')) {
        // Return the second element for prefixed format (device!device_type_id or part!part_type_id)
        return deviceTypeDefinitionId[1];
    }

    // Return the first element for legacy format (device_type_id!manufacturer_id!model_id)
    return deviceTypeDefinitionId[0];
}

export function getManufacturerId(deviceTypeDefinitions) {
    if (deviceTypeDefinitions === undefined) {
        return null;
    }

    if (deviceTypeDefinitions.trim() === "") {
        return null;
    }

    // Split this ID
    const deviceTypeDefinitionId = deviceTypeDefinitions.split('!');

    // Check if this is a prefixed format (device! or part!)
    if (deviceTypeDefinitionId.length > 0 && (deviceTypeDefinitionId[0] === 'device' || deviceTypeDefinitionId[0] === 'part')) {
        // For prefixed format: device!device_type_id!manufacturer_id!model_id
        // Return the third element (manufacturer_id) if it exists
        return deviceTypeDefinitionId.length > 2 ? deviceTypeDefinitionId[2] : null;
    }

    // For legacy format: device_type_id!manufacturer_id!model_id
    // Return the second element (manufacturer_id)
    return deviceTypeDefinitionId.length > 1 ? deviceTypeDefinitionId[1] : null;
}

export function getModelId(deviceTypeDefinitions) {
    if (deviceTypeDefinitions === undefined) {
        return null;
    }

    if (deviceTypeDefinitions.trim() === "") {
        return null;
    }

    // Split this ID
    const deviceTypeDefinitionId = deviceTypeDefinitions.split('!');

    // Check if this is a prefixed format (device! or part!)
    if (deviceTypeDefinitionId.length > 0 && (deviceTypeDefinitionId[0] === 'device' || deviceTypeDefinitionId[0] === 'part')) {
        // For prefixed format: device!device_type_id!manufacturer_id!model_id
        // Return the fourth element (model_id) if it exists
        return deviceTypeDefinitionId.length > 3 ? deviceTypeDefinitionId[3] : null;
    }

    // For legacy format: device_type_id!manufacturer_id!model_id
    // Return the third element (model_id)
    return deviceTypeDefinitionId.length > 2 ? deviceTypeDefinitionId[2] : null;
}